import { request } from "./http";

export interface AccountGroup {
  id: string;
  name: string;
  mode: "PAPER" | "LIVE";
  base_currency: string;
  default_routing: "A_BOOK" | "B_BOOK" | "MIXED";
  markup_bps_bid: string;
  markup_bps_ask: string;
  commission_per_lot: string;
  swap_long_bps: string;
  swap_short_bps: string;
  im_pct: string;
  mc_level_pct: string;
  so_level_pct: string;
  hedge_mode: "AUTO" | "MANUAL" | "OFF";
  hedge_threshold_lots: string;
  b_book_pct: string;
  b_book_matcher: "L1" | "L2";
  created_at: string;
  updated_at: string;
}

export interface AccountGroupCreate {
  name: string;
  mode?: "PAPER" | "LIVE";
  base_currency?: string;
  default_routing?: "A_BOOK" | "B_BOOK" | "MIXED";
  markup_bps_bid?: string | number;
  markup_bps_ask?: string | number;
  commission_per_lot?: string | number;
  swap_long_bps?: string | number;
  swap_short_bps?: string | number;
  im_pct?: string | number;
  mc_level_pct?: string | number;
  so_level_pct?: string | number;
  hedge_mode?: "AUTO" | "MANUAL" | "OFF";
  hedge_threshold_lots?: string | number;
  b_book_pct?: string | number;
  b_book_matcher?: "L1" | "L2";
}

export type AccountGroupPatch = Partial<AccountGroupCreate>;

export interface TradingAccount {
  id: string;
  owner_user_id: string;
  account_group_id: string;
  login_code: string;
  label: string | null;
  base_currency: string;
  balance: string;
  credit: string;
  equity: string;
  margin_used: string;
  status: "active" | "closed" | "locked";
  created_at: string;
  updated_at: string;
}

export interface TradingAccountCreate {
  account_group_id: string;
  label?: string;
  base_currency?: string;
  login_code?: string;
}

export interface FundRequest {
  amount: string | number;
  entry_type: "deposit" | "withdrawal" | "adjustment";
  currency?: string;
  note?: string;
}

export interface LedgerEntry {
  id: number;
  entry_type: string;
  amount: string;
  currency: string;
  reference_id: string | null;
  note: string | null;
  occurred_at: string;
}

export const accountGroups = {
  list: () => request<AccountGroup[]>("/api/v1/account-groups"),
  get: (id: string) => request<AccountGroup>(`/api/v1/account-groups/${id}`),
  create: (body: AccountGroupCreate) =>
    request<AccountGroup>("/api/v1/account-groups", { body }),
  patch: (id: string, body: AccountGroupPatch) =>
    request<AccountGroup>(`/api/v1/account-groups/${id}`, { method: "PATCH", body }),
};

export const tradingAccounts = {
  list: (mineOnly = true) =>
    request<TradingAccount[]>(`/api/v1/trading-accounts?mine_only=${mineOnly}`),
  get: (id: string) => request<TradingAccount>(`/api/v1/trading-accounts/${id}`),
  create: (body: TradingAccountCreate) =>
    request<TradingAccount>("/api/v1/trading-accounts", { body }),
  patch: (id: string, body: { label?: string; status?: "active" | "closed" | "locked" }) =>
    request<TradingAccount>(`/api/v1/trading-accounts/${id}`, { method: "PATCH", body }),
  fund: (id: string, body: FundRequest) =>
    request<TradingAccount>(`/api/v1/trading-accounts/${id}/fund`, { body }),
  ledger: (id: string, limit = 200) =>
    request<LedgerEntry[]>(`/api/v1/trading-accounts/${id}/ledger?limit=${limit}`),
};
